import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react';
import { getSermons } from '../../lib/api';
import { adminCreateSermon, adminUpdateSermon, adminDeleteSermon } from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';

const EMPTY = { title: '', speaker: '', series: '', date: '', scripture: '', videoType: 'youtube', videoUrl: '', description: '' };
const input = 'w-full bg-white border border-[#d8d2c6] px-3 py-2.5 outline-none focus:border-[#17565f] transition-colors rounded';

// Extract a YouTube id from any common URL shape
const extractYouTubeId = (url) => {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : '';
};

const SermonsManager = () => {
  const { toast } = useToast();
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSermons(await getSermons());
    } catch (err) {
      console.error('Failed to load sermons:', err);
      toast({ title: 'Could not load sermons', description: 'Please refresh and try again.' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s, videoUrl: s.videoUrl || (s.youtubeId ? `https://youtu.be/${s.youtubeId}` : '') }); setEditing(s.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    payload.youtubeId = payload.videoType === 'youtube' ? extractYouTubeId(payload.videoUrl) : '';
    try {
      if (editing === 'new') { await adminCreateSermon(payload); toast({ title: 'Sermon added' }); }
      else { await adminUpdateSermon(editing, payload); toast({ title: 'Sermon updated' }); }
      close(); load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Check the fields.' });
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try { await adminDeleteSermon(s.id); toast({ title: 'Sermon deleted' }); load(); }
    catch (err) { console.error('Delete sermon failed:', err); toast({ title: 'Delete failed' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif-display text-3xl text-[#0f2e38]">Sermons</h2>
          <p className="text-[#5a6b71] mt-1">Add past messages with a YouTube or Facebook video link. These replace the “Coming Soon” placeholders on the site.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#17565f] text-white hover:bg-[#0f2e38] px-5 py-3 rounded font-600 transition-colors">
          <Plus className="h-4 w-4" /> New Sermon
        </button>
      </div>

      {loading ? (
        <p className="text-[#5a6b71]">Loading…</p>
      ) : sermons.length === 0 ? (
        <p className="text-[#5a6b71]">No sermons yet. The site shows a “Coming Soon” section until you add one.</p>
      ) : (
        <div className="grid gap-4">
          {sermons.map((s) => (
            <div key={s.id} className="bg-white border border-[#e4ddd0] rounded-lg p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-600 text-[#0f2e38] truncate">{s.title}</p>
                <p className="text-sm text-[#5a6b71]">{s.speaker} · {s.date} · {(s.videoType || 'youtube') === 'facebook' ? 'Facebook' : 'YouTube'}</p>
              </div>
              <button onClick={() => openEdit(s)} className="p-2 text-[#17565f] hover:bg-[#f0ebe0] rounded transition-colors"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(s)} className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={save} className="bg-white rounded-xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#e4ddd0] sticky top-0 bg-white">
              <h3 className="font-serif-display text-2xl text-[#0f2e38]">{editing === 'new' ? 'New Sermon' : 'Edit Sermon'}</h3>
              <button type="button" onClick={close} className="p-2 hover:bg-[#f0ebe0] rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-sm text-[#3a4d54] mb-1">Title</label><input required value={form.title} onChange={set('title')} className={input} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="block text-sm text-[#3a4d54] mb-1">Speaker</label><input required value={form.speaker} onChange={set('speaker')} className={input} /></div>
                <div><label className="block text-sm text-[#3a4d54] mb-1">Date</label><input required type="date" value={form.date} onChange={set('date')} className={input} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="block text-sm text-[#3a4d54] mb-1">Series (optional)</label><input value={form.series} onChange={set('series')} className={input} /></div>
                <div><label className="block text-sm text-[#3a4d54] mb-1">Scripture (optional)</label><input value={form.scripture} onChange={set('scripture')} placeholder="John 3:16" className={input} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className="block text-sm text-[#3a4d54] mb-1">Video source</label>
                  <select value={form.videoType} onChange={set('videoType')} className={input}>
                    <option value="youtube">YouTube</option>
                    <option value="facebook">Facebook</option>
                  </select>
                </div>
                <div className="sm:col-span-2"><label className="block text-sm text-[#3a4d54] mb-1">Video link</label><input required value={form.videoUrl} onChange={set('videoUrl')} placeholder={form.videoType === 'facebook' ? 'https://www.facebook.com/.../videos/...' : 'https://youtu.be/...'} className={input} /></div>
              </div>
              <div><label className="block text-sm text-[#3a4d54] mb-1">Description (optional)</label><textarea rows={3} value={form.description} onChange={set('description')} className={input} /></div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-[#e4ddd0] sticky bottom-0 bg-white">
              <button type="button" onClick={close} className="px-5 py-2.5 border border-[#d8d2c6] rounded hover:bg-[#f0ebe0] transition-colors">Cancel</button>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#c9a24b] text-[#0b2027] hover:bg-[#d8b566] px-6 py-2.5 rounded font-700 transition-colors">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SermonsManager;
